


import React, { useCallback, useEffect, useState } from 'react';
import { CONFIG } from 'config/config';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
	EnvelopesWrapper,
	InputCheck,
	NavigationWrapper,
	PickerHOC,
	Text,
	UiWrapper,
	UserWrapper,
	View,
} from '@coinscrap/webapp-core';

import { Wrapper } from 'layout/components/Body/Wrapper';
import Footer from 'layout/components/Footer/Footer';
import Header from 'layout/components/Header/Header';

import BackNextButton from 'components/BackNextButton/BackNextButton';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { MainModal } from 'components/MainModal/MainModal';
import DocView from 'components/MyDocViewInScreen/MyDocViewInScreen';

import { useGetError } from 'hooks/useGetError';
import { useInitialScrollToTop } from 'hooks/useInitialScrollToTop';

import Download from 'assets/images/svgDynamic/Download';
import Pdf from 'assets/images/svgDynamic/Pdf';

import { downloadDoc } from 'helpers/downloadDoc';

import { useCoinscrapPageView } from '../../hooks/useCoinscrapPageView';
import * as S from './styles';

export const SignatureRead = PickerHOC(
	{
		envelope: {
			sourcePath: 'envelopes',
			sourceWrapper: EnvelopesWrapper,
			arrayItemMatcher: {
				type: 'param',
				paramName: 'envelopeId',
				itemPropertyPath: 'id',
			},
		},
		user: {
			sourceWrapper: UserWrapper,
			sourcePath: 'user',
		},
	},
	LoadingScreen,
)(() => {
	const { navigateTo } = NavigationWrapper.use();
	const { updateUser } = UserWrapper.use();
	const { getPreSignedDocumentUrl } = EnvelopesWrapper.use();
	const { envelope, user } = PickerHOC.use();
	const { useHeader, useFooter, useLoading, openModal } = UiWrapper.use();
	const { t, i18n } = useTranslation();

	const [loading, setLoading] = useState();

	useFooter(<Footer />, []);
	useHeader(<Header />, []);
	useLoading(loading, { loadingMessage: loading });
	useInitialScrollToTop();
	useCoinscrapPageView('SignatureRead');
	const getError = useGetError();

	// ESTADO
	const [documents, setDocuments] = useState(envelope.documents);
	const [documentsToRead, setDocumentsToRead] = useState([]);
	const [currentDoc, setCurrentDoc] = useState(undefined);
	const [modalDocOpen, setModalDocOpen] = useState(false);
	const [read, setRead] = useState(
		(documents || []).reduce(
			(acc, act) => ({
				...acc,
				[act.id]: envelope.signStatus !== 'PENDING' ? true : false,
			}),
			{},
		),
	);

	useEffect(() => {
		async function fetchPreSignedUrls() {
			let docs = envelope.documents
				.filter((d) => d.name !== 'documento_identidad_01')
				.filter((d) => d.name !== 'documento_identidad_02');

			const docsWithPresignedUrl = await Promise.all(
				docs.map(async (doc) => {
					const { url: preSignedDocumentUrl } = await getPreSignedDocumentUrl(doc.id);
					return {
						...doc,
						preSignedDocumentUrl,
					};
				}),
			);
			setDocuments(docsWithPresignedUrl);
		}
		fetchPreSignedUrls();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// NAVIGATION
	const next = async () => {
		try {
			setLoading('Guardando...');
			await updateUser({
				...user,
				metadata: {
					...user?.metadata,
					docsRead: true,
				},
			});
			CONFIG.routing.signatureRead.next(navigateTo);
		} catch (e) {
			setLoading(undefined);
			openModal(() => <MainModal type="error" text={getError(e)} />, {
				disableClickAway: true,
				closeButton: true,
				modalClass: isMobile ? 'modal-xl' : 'modal-main',
				key: 'modal-SignatureRead-KO',
			});
		}
	};

	const [readStep, setReadStep] = useState(1);
	const [allDocsRead, setAllDocsRead] = useState(false);
	const [subtitle, setSubtitle] = useState('');

	const openDocModal = () => {
		setModalDocOpen(true);
		setReadStep(2);
	};

	useEffect(() => {
		if (modalDocOpen || allDocsRead) {
			setSubtitle('');
		} else {
			if (readStep === 1) {
				setSubtitle(
					'Vas a proceder a la lectura y firma de los documentos asociados a tu póliza. Por favor, sigue los pasos que se te indican a continuación.',
				);
			} else {
				setSubtitle(
					'Por favor, abre los documentos que se muestran a continuación, léelos y márcalos como leídos.',
				);
			}
		}
	}, [allDocsRead, modalDocOpen, readStep]);

	const isDisabled = useCallback(() => {
		return documents && documents.find((d) => !read?.[d.id]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [read]);

	useEffect(() => {
		setAllDocsRead(!isDisabled());
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDisabled]);

	// EFFECTS
	useEffect(() => {
		let docs = envelope.documents;
		const existsIdentityDocument01 = docs.find((doc) => doc.name === 'documento_identidad_01');
		if (existsIdentityDocument01) {
			docs = docs.filter((d) => d.name !== 'documento_identidad_01');
		}
		const existsIdentityDocument02 = docs.find((doc) => doc.name === 'documento_identidad_02');
		if (existsIdentityDocument02) {
			docs = docs.filter((d) => d.name !== 'documento_identidad_02');
		}
		setDocuments(docs);
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		console.log('📌 envelope', envelope);
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [envelope]);

	useEffect(() => {
		console.log('💢 DOCUMENTS READ ', read);
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [read]);

	return (
		<Wrapper showTopSection step={6} title="Documentos y firma" subtitle={subtitle}>
			<View innerWrapperSt>
				<S.StepWrapper>
					<S.StepWrapperItem>
						{modalDocOpen ? (
							<>
								<S.StepCircle>2</S.StepCircle>
								<Text stepsSt>{t('signatureSteps.feature1')}</Text>
							</>
						) : (
							<>
								{allDocsRead ? (
									<>
										<S.StepCircle>3</S.StepCircle>
										<Text stepsSt>{t('signatureSteps.feature2')}</Text>
									</>
								) : (
									<>
										<S.StepCircle>1</S.StepCircle>
										<Text stepsSt>{t('signatureSteps.feature')}</Text>
									</>
								)}
							</>
						)}
					</S.StepWrapperItem>
				</S.StepWrapper>
				{!modalDocOpen && (
					<>
						{(documents || []).map((d) => (
							<S.DocumentWrapper key={d.id}>
								<S.DocumentWrapperText>
									{CONFIG.views.signatureRead.docNames[d.name] || d.name}
								</S.DocumentWrapperText>
								<S.DocumentWrapperButtons>
									<Pdf
										style={{
											cursor: 'pointer',
											fill: read?.[d.id]
												? CONFIG.colors.primary
												: CONFIG.colors.grey,
										}}
										onClick={() => {
											//OPEN IN MODAL
											setCurrentDoc(d);
											openDocModal();
										}}
									/>
									<Download
										style={{
											cursor: 'pointer',
											fill: read?.[d.id]
												? CONFIG.colors.primary
												: CONFIG.colors.grey,
										}}
										onClick={() =>
											downloadDoc(
												d.preSignedDocumentUrl,
												CONFIG.views.signatureRead.docNames[d.name],
											)
										}
									/>
									<InputCheck
										style={{
											padding: 4,
										}}
										//disabled={true}
										checkedIcon={<CheckBoxOutlinedIcon />}
										value={read?.[d.id]}
										onChange={() => {
											//OPEN IN MODAL
											setCurrentDoc(d);
											openDocModal();
										}}
									/>
								</S.DocumentWrapperButtons>
							</S.DocumentWrapper>
						))}
					</>
				)}
				{modalDocOpen && (
					<DocView
						open={modalDocOpen}
						title={
							CONFIG.views.signatureRead.docNames[currentDoc.name] || currentDoc.name
						}
						onClose={() => {
							setModalDocOpen(false);
							setRead((prevState) => {
								return { ...prevState, [currentDoc.id]: true };
							});
						}}
						url={currentDoc.preSignedDocumentUrl}
						styleType="white"
						disk
					/>
				)}
			</View>

			<BackNextButton isBackShown={false} onClickNext={next} isNextDisabled={isDisabled()} />
		</Wrapper>
	);
});
